import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

/** Reserved for per-link router options (replace, state, scroll). Empty today; augment it with `declare module`. */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LinkNavigateOptions {}

export interface LinkRouterConfig {
    /**
     * Perform client-side navigation. Receives the href exactly as the caller wrote it (un-prefixed: the host
     * router applies its own basename). May return anything (e.g. Next's Promise); the return value is ignored.
     */
    navigate: (href: string, options?: LinkNavigateOptions) => void;
    /**
     * Optional. Maps an app-relative href to the URL the browser should display (basename, basePath or
     * hash-router aware). Called as a hook during render, so it must be a stable function reference for the
     * provider's lifetime and must not read browser globals (it also runs during server rendering).
     */
    useHref?: (href: string) => string;
}

export interface LinkProviderProps {
    /** Pass `null` to declare "native links on purpose" and silence the development warning. */
    navigate: LinkRouterConfig['navigate'] | null;
    useHref?: LinkRouterConfig['useHref'];
    children: React.ReactNode;
}

/** `undefined` = no provider mounted; `null` = a provider declared native links on purpose. */
const LinkRouterContext = createContext<LinkRouterConfig | null | undefined>(undefined);

const isDevelopment = process.env.NODE_ENV !== 'production';

const LinkProvider: React.FC<LinkProviderProps> = ({ navigate, useHref, children }) => {
    // `navigate` only runs at event time, so the latest one is kept in a ref and the context value stays stable
    // even when a consumer passes a fresh function on every render.
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    const stableNavigate = useCallback<LinkRouterConfig['navigate']>((href, options) => {
        navigateRef.current?.(href, options);
    }, []);

    const isNative = navigate === null;
    const value = useMemo<LinkRouterConfig | null>(
        () => (isNative ? null : { navigate: stableNavigate, useHref }),
        [isNative, stableNavigate, useHref]
    );

    // `useHref` is called as a hook by every Link, so a changing identity would change hook order downstream.
    const previousUseHref = useRef(useHref);
    useEffect(() => {
        if (isDevelopment && previousUseHref.current !== useHref) {
            console.error(
                'LinkProvider: the `useHref` prop changed identity between renders. It is called as a hook, so it ' +
                    'must be a stable function (define it at module scope or memoise it).'
            );
        }
        previousUseHref.current = useHref;
    }, [useHref]);

    return <LinkRouterContext.Provider value={value}>{children}</LinkRouterContext.Provider>;
};

LinkProvider.displayName = 'LinkProvider';

/** The host router config, or `null` outside a `LinkProvider` (or inside one with `navigate={null}`). Never throws. */
export const useLinkRouter = (): LinkRouterConfig | null => useContext(LinkRouterContext) ?? null;

let hasWarnedMissingProvider = false;

/**
 * Internal, for components that render links (`Link`): in development, warns once per session when a link that
 * would navigate client-side mounts with no `LinkProvider` at all. Runs in an effect, so never during server render.
 */
export const useMissingLinkProviderWarning = (isInternal: boolean): void => {
    const context = useContext(LinkRouterContext);
    useEffect(() => {
        if (isDevelopment && isInternal && context === undefined && !hasWarnedMissingProvider) {
            hasWarnedMissingProvider = true;
            console.warn(
                'Link: no LinkProvider found; internal links will do full page loads. Wrap your app in ' +
                    'LinkProvider, or pass navigate={null} to silence this.'
            );
        }
    }, [isInternal, context]);
};

/** Test-only: re-arms the once-per-session missing-provider warning. Deliberately not exported from the barrel. */
export const resetMissingLinkProviderWarning = (): void => {
    hasWarnedMissingProvider = false;
};

export default LinkProvider;
